// The brain's environment contract — TODO T17 (30 Aug 2026).
//
// Same rule as every other workspace (services/bff/src/config.ts, packages/db/src/env.ts): no
// fallbacks, every problem reported at once, and the message says what to do. A default that is
// nearly right fails silently, and here the silent failure is a sign-in page that never signs
// anyone in.
//
// Only `VITE_`-prefixed variables exist in a browser bundle, so this file can never read the
// database URLs or ANTHROPIC_API_KEY that sit in the same .env — see vite.config.ts on envDir.

export interface ConsoleEnv {
  /**
   * Clerk's PUBLISHABLE key (`pk_test_…` / `pk_live_…`) — the browser half of the pair whose
   * other half is the BFF's `CLERK_JWT_KEY`. It is public by design: it names the instance and
   * nothing more. The two must come from the SAME Clerk instance or every token this app mints
   * fails verification there, which is the most confusing way this can be wrong.
   */
  clerkPublishableKey: string;
  /**
   * Where the BFF answers, origin only — `http://localhost:8081` locally, the brain's second
   * ALB hostname deployed (T14a). Whatever this is, the brain's OWN origin has to be in that
   * service's CONSOLE_ORIGINS, which is both its CORS allowlist and Clerk's `azp` check.
   */
  bffUrl: string;
  /**
   * Tap to talk's engine (T57, 10 Sep 2026) — `transcribe`, `browser`, or null for no mic at all,
   * which is what an unset variable means. Optional, like the voice output and the release, and
   * off by default on purpose. `transcribe` is Amazon Transcribe (T57b), the engine Natanel chose for real
   * candidates, which the release pipeline sets. `browser` sends the audio to the browser vendor,
   * so it is for local trials on invented data — and src/voice-engine-decision.test.ts keeps it out
   * of the release for good.
   */
  voiceInput: 'transcribe' | 'browser' | null;
  /**
   * Her voice (T66, 13 Sep 2026) — `polly`, or null for the device's own voice, which is what an
   * unset variable means. `polly` is Amazon Polly through the brain API, on kadmon's own
   * account: Natanel's decision, and what the release pipeline sets, because the device's voice
   * sounds like a machine and reads a slash as "slash". Nothing without a mic: no input, no voice.
   */
  voiceOutput: 'polly' | null;
  /**
   * T160 (28 Sep 2026): the release this bundle was built from, the commit the pipeline also
   * tags the brain API's image with (VITE_RELEASE). Each import sends it, and the API tells a page
   * from another release to reload rather than import (services/bff/src/release.ts). Unset
   * locally, like the API's own, which checks nothing.
   */
  release: string | null;
}

export class EnvError extends Error {
  override readonly name = 'EnvError';
}

/** What Vite exposes. Narrowed here so the reader below is testable without a bundler. */
export type EnvSource = Record<string, string | boolean | undefined>;

function read(source: EnvSource, name: string): string | null {
  const value = source[name];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

export function readEnv(source: EnvSource): ConsoleEnv {
  const clerkPublishableKey = read(source, 'VITE_CLERK_PUBLISHABLE_KEY');
  const bffUrl = read(source, 'VITE_BFF_URL');
  const voiceInput = read(source, 'VITE_VOICE_INPUT');
  const voiceOutput = read(source, 'VITE_VOICE_OUTPUT');
  const release = read(source, 'VITE_RELEASE');

  const problems: string[] = [];
  if (!clerkPublishableKey) {
    problems.push(
      'VITE_CLERK_PUBLISHABLE_KEY is missing. Clerk dashboard -> Configure -> API keys -> ' +
        'Publishable key (it starts pk_test_ on the development instance).',
    );
  }
  if (!bffUrl) {
    problems.push('VITE_BFF_URL is missing. Locally that is http://localhost:8081.');
  } else if (bffUrl.endsWith('/')) {
    // Every path an api/ module builds starts with a slash, so a trailing one here produces
    // `//api/v1` — which some proxies serve, some 404, and none of them explain.
    problems.push(`VITE_BFF_URL must be an origin with no trailing slash: got ${bffUrl}`);
  }
  if (voiceInput !== null && voiceInput !== 'transcribe' && voiceInput !== 'browser') {
    // An engine name this build does not have is refused rather than read as "no mic": whoever
    // typed it expected a mic, and a brain that quietly has none looks like a broken button.
    problems.push(
      `VITE_VOICE_INPUT is "transcribe", "browser", or unset for no mic at all: got "${voiceInput}".`,
    );
  }

  if (voiceOutput !== null && voiceOutput !== 'polly') {
    problems.push(
      `VITE_VOICE_OUTPUT is "polly", or unset for the device's own voice: got "${voiceOutput}".`,
    );
  }

  if (problems.length > 0) {
    throw new EnvError(
      'The brain cannot start:\n\n' +
        problems.map((problem) => `  - ${problem}`).join('\n') +
        '\n\n  These come from the repo-root .env (see .env.example). Vite reads it because of ' +
        'envDir in vite.config.ts, and only VITE_-prefixed names reach the browser.\n',
    );
  }

  return {
    clerkPublishableKey: clerkPublishableKey!,
    bffUrl: bffUrl!,
    voiceInput: voiceInput === 'transcribe' || voiceInput === 'browser' ? voiceInput : null,
    voiceOutput: voiceOutput === 'polly' ? 'polly' : null,
    release,
  };
}

// `VITE_COMPOSE_ENABLED` lived here between T19 and T19a (3 Sep 2026, both): a preview flag for
// the compose bar while nothing could send. The send lane is wired now, so the flag is gone — a
// line still in a `.env` is read by nothing and harms nothing.
